import { Action, createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';

import * as LocationsActions from './locations.actions';
import {
  IAdministrator,
  ILocation,
} from '@boma/supplier-portal/shared/entities';
import { IRole } from '@boma/client-shared';

export const LOCATIONS_FEATURE_KEY = 'locations';

export interface locationState extends EntityState<ILocation> {
  selectedId?: string | number; // which Locations record has been selected
  loaded: boolean; // has the Locations list been loaded
  query: string | null;
  error?: string | null; // last known error (if any),
  administrators: IAdministrator[];
  selectedAdministratorId: string | null;
  roles: IRole[];
  pagination: {};
  nextPage?: number;
  perPage?: number;
}

export interface LocationsPartialState {
  readonly [LOCATIONS_FEATURE_KEY]: locationState;
}

export const locationsAdapter: EntityAdapter<ILocation> = createEntityAdapter<
  ILocation
>();

export const locationInitialState: locationState = locationsAdapter.getInitialState(
  {
    // set initial required properties
    query: '',
    loaded: false,
    administrators: [],
    roles: [],
    selectedAdministratorId: null,
    nextPage: 1,
    perPage: 250, //todo this is nuts
    pagination: {},
  }
);

const locationsReducer = createReducer(
  locationInitialState,
  on(LocationsActions.setLocationPage, (state, { page }) => ({
    ...state,
    page: page,
  })),

  on(LocationsActions.setLocationQuery, (state, { query }) => ({
    ...state,
    query: query,
  })),

  on(LocationsActions.loadLocations, (state) => ({
    ...state,
    loaded: false,
    error: null,
  })),
  on(LocationsActions.loadLocationsSuccess, (state, { payload }) =>
    locationsAdapter.setAll(payload.items, {
      ...state,
      loaded: true,
      pagination: {
        totalItems: payload.totalItems,
        totalPages: payload.totalPages,
        previousPage: payload.previousPage,
        nextPage: payload.nextPage,
      },
      nextPage: payload.nextPage,
    })
  ),
  on(LocationsActions.loadLocationsFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(LocationsActions.createLocationSuccess, (state, action) => {
    return locationsAdapter.upsertOne(action.location, {
      ...state,
      error: '',
    });
  }),
  on(LocationsActions.createLocationFailure, (state, { error }) => {
    return {
      ...state,
      error: error,
    };
  }),
  on(LocationsActions.loadLocationSuccess, (state, { location }) =>
    locationsAdapter.upsertOne(location, {
      ...state,
      selectedId: location.id,
    })
  ),
  on(LocationsActions.clearSelectedLocation, (state) => ({
    ...state,
    selectedId: null,
  })),
  on(LocationsActions.loadLocationFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(LocationsActions.updateLocationSuccess, (state, { location }) =>
    locationsAdapter.upsertOne(location, {
      ...state,
      selectedId: location.id,
    })
  ),
  on(LocationsActions.updateLocationFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(
    LocationsActions.loadLocationAdministratorsSuccess,
    (state, { administrators }) => ({
      ...state,
      administrators,
    })
  ),
  on(LocationsActions.loadLocationRolesSuccess, (state, { payload }) => ({
    ...state,
    roles: payload.items,
  })),
  on(
    LocationsActions.createLocationAdministratorSuccess,
    (state, { administrator }) => ({
      ...state,
      administrators: state.administrators?.length
        ? [...state.administrators].concat([administrator])
        : [administrator],
    })
  ),
  on(
    LocationsActions.deleteLocationAdministratorSuccess,
    (state, { userId }) => ({
      ...state,
      administrators: state.administrators?.length
        ? state.administrators.filter((a) => a.id != userId)
        : [],
    })
  ),
  on(
    LocationsActions.loadLocationAdministratorDetails,
    (state, { adminId }) => ({
      ...state,
      selectedAdministratorId: null,
    })
  ),
  on(
    LocationsActions.loadLocationAdministratorDetailsSuccess,
    (state, { administrator }) => ({
      ...state,
      administrators: state.administrators?.length
        ? [
            ...state.administrators.filter((a) => a.id != administrator.id),
          ].concat(administrator)
        : [administrator],
      selectedAdministratorId: administrator.id,
    })
  )
);

export function locationReducer(
  state: locationState | undefined,
  action: Action
) {
  return locationsReducer(state, action);
}
